import { z } from 'zod';
import { PlatformSchema } from './post-contents.js';
import { AttachOverrideSchema, OverrideReasonSchema } from './hotel-media.js';

export const ImageSourceSchema = z.enum(['library', 'upload', 'ai_generated']);
export type ImageSource = z.infer<typeof ImageSourceSchema>;

export const PostImageSchema = z.object({
  id: z.string().guid(),
  postId: z.string().guid(),
  platform: PlatformSchema,
  source: ImageSourceSchema,
  storageKey: z.string().nullable(),
  hotelMediaId: z.string().guid().nullable(),
  position: z.number().int(),
  isCurrent: z.boolean(),
  createdAt: z.string().datetime(),
  url: z.string().url().nullable(),
  /**
   * Admin-override evidence carried BY THIS ATTACHMENT (galéria-revízió spec
   * 9.3). Non-null `overrideReason` means a blocked image was deliberately
   * attached here; `copy()` reads it to carry the waiver onto the other
   * platform instead of re-deciding from the media row, where the waiver
   * leaves no trace. All four move together.
   */
  overrideReason: OverrideReasonSchema.nullable(),
  overrideNote: z.string().nullable(),
  overrideBy: z.string().guid().nullable(),
  overrideAt: z.string().datetime().nullable(),
});
export type PostImage = z.infer<typeof PostImageSchema>;

export const SetPostImagesSchema = z.object({
  platform: PlatformSchema,
  hotelMediaIds: z.array(z.string().guid()).max(10),
  /**
   * Admin-only escape hatch for a blocked image (galéria-revízió spec 9.3).
   * The compliance verdict comes from `evaluateRow` in the service; a
   * non-admin sender is rejected regardless of whether this is present.
   */
  override: AttachOverrideSchema.optional(),
});
export type SetPostImages = z.infer<typeof SetPostImagesSchema>;

export const ReorderPostImagesSchema = z.object({
  platform: PlatformSchema,
  hotelMediaIds: z.array(z.string().guid()).max(10),
});
export type ReorderPostImages = z.infer<typeof ReorderPostImagesSchema>;

/**
 * Copy the current image set of one platform onto the other, as-is: library
 * refs, uploads and AI-generated pictures alike (the set endpoint can only
 * take library refs). The target's previous current set is demoted.
 */
export const CopyPostImagesSchema = z
  .object({
    from: PlatformSchema,
    to: PlatformSchema,
  })
  .refine((v) => v.from !== v.to, { message: 'from and to must differ', path: ['to'] });
export type CopyPostImages = z.infer<typeof CopyPostImagesSchema>;
